import { useEffect } from 'react';
import { FormDataPath, ReformReturn } from '../../../types';
import { FieldError } from 'react-hook-form';
import { FormErrorsState } from './types';

export const useReformErrors = <T extends Record<string, any>>(
  reform: ReformReturn<T>
): FormErrorsState<T> => {
  const {
    formState,
    setError,
    clearErrors: clearFormErrors,
  } = reform.formMethods;

  useEffect(() => {
    // You can add logging here for debugging
    if (Object.keys(formState.errors || {}).length > 0) {
      console.log('Current form errors:', formState.errors);
    }
  }, [formState.errors]);

  // Helper function to extract error message from various error formats
  const extractErrorMessage = (error: any): string | undefined => {
    if (!error) return undefined;

    // Handle array of errors (like in dateRange)
    if (Array.isArray(error)) {
      // Return the first error message in the array
      return error[0]?.message;
    }

    // Handle standard error object with message
    return error.message;
  };

  // Type-safe way to access errors with string keys
  const getErrorByKey = (key: string): any => {
    // Use type assertion to access errors with string keys
    return (formState.errors as Record<string, any>)[key];
  };

  const getError = (groupIndex: number, field: string): string | undefined => {
    // First check if the error is in the flattened format (e.g., "0.users")
    const flatKey = `${groupIndex}.${field}`;
    const flatError = getErrorByKey(flatKey);

    if (flatError) {
      return extractErrorMessage(flatError);
    }

    // If not found in flat format, check in the nested format
    const fieldError = formState.errors?.groups?.[groupIndex]?.data as
      | Record<string, any>
      | undefined;

    if (!fieldError) return undefined;

    return extractErrorMessage(fieldError[field]);
  };

  const getFieldError = (
    groupIndex: number,
    fieldName: string
  ): string | undefined => {
    return getError(groupIndex, fieldName);
  };

  const getGroupErrors = (groupIndex: number): Record<string, string> => {
    const result: Record<string, string> = {};

    // Check for flattened errors first
    Object.keys(formState.errors || {}).forEach(key => {
      // Check if the key matches the pattern "groupIndex.fieldName"
      const match = key.match(`^${groupIndex}\\.(.+)$`);
      if (match) {
        const [, fieldName] = match;
        const errorMessage = extractErrorMessage(getErrorByKey(key));
        if (errorMessage) {
          result[fieldName] = errorMessage;
        }
      }
    });

    // If no flattened errors found, check nested format
    if (Object.keys(result).length === 0) {
      const groupData = formState.errors?.groups?.[groupIndex]?.data as
        | Record<string, any>
        | undefined;

      if (groupData) {
        Object.keys(groupData).forEach(field => {
          const errorMessage = extractErrorMessage(groupData[field]);
          if (errorMessage) {
            result[field] = errorMessage;
          }
        });
      }
    }

    return result;
  };

  const getFieldErrors = (
    index: number
  ): Record<keyof T, string | undefined> => {
    const result = {} as Record<keyof T, string | undefined>;

    // Check for flattened errors first
    Object.keys(formState.errors || {}).forEach(key => {
      const match = key.match(`^${index}\\.(.+)$`);
      if (match) {
        const [, fieldName] = match;
        result[fieldName as keyof T] = extractErrorMessage(getErrorByKey(key));
      }
    });

    // If no flattened errors found, check nested format
    if (Object.keys(result).length === 0) {
      const groupData = formState.errors?.groups?.[index]?.data as
        | Record<string, any>
        | undefined;

      if (groupData) {
        Object.keys(groupData).forEach(field => {
          result[field as keyof T] = extractErrorMessage(groupData[field]);
        });
      }
    }

    return result;
  };

  // Error Clearers
  const clearErrors = () => {
    clearFormErrors();
  };

  const clearFieldError = (groupIndex: number, fieldName: string) => {
    // The issue is here - clearFormErrors might only accept a string or undefined
    // Let's fix by ensuring we're passing the correct type
    const fieldPath = `groups.${groupIndex}.data.${fieldName}` as FormDataPath<T>;
    clearFormErrors(fieldPath as any);
  };

  const clearGroupError = (groupIndex: number) => {
    const groupPath = `groups.${groupIndex}` as FormDataPath<T>;
    clearFormErrors(groupPath as any);
  };

  const clearGlobalError = () => {
    clearFormErrors('root' as FormDataPath<T> as any);
  };

  // // Error Setters
  // const setFieldError = (
  //   groupIndex: number,
  //   fieldName: string,
  //   message: string
  // ) => {
  //   const fieldPath = `groups.${groupIndex}.data.${fieldName}` as FormDataPath<
  //     T
  //   >;
  //   setError(fieldPath, {
  //     type: 'manual',
  //     message,
  //   });
  // };

  // const setGroupError = (groupIndex: number, message: string) => {
  //   const groupPath = `groups.${groupIndex}` as FormDataPath<T>;
  //   setError(groupPath, {
  //     type: 'manual',
  //     message,
  //   });
  // };

  // const setGlobalError = (message: string) => {
  //   setError('root' as FormDataPath<T>, {
  //     type: 'manual',
  //     message,
  //   });
  // };

  const hasGroupErrors = (groupIndex: number): boolean => {
    // Check for flattened errors first
    for (const key in formState.errors || {}) {
      if (key.startsWith(`${groupIndex}.`)) {
        return true;
      }
    }

    // If no flattened errors, check nested format
    return (
      !!formState.errors?.groups?.[groupIndex]?.data &&
      Object.keys(
        (formState.errors?.groups?.[groupIndex]?.data as object) || {}
      ).length > 0
    );
  };

  const getAllErrors = (): Record<string, FieldError> => {
    const allErrors: Record<string, FieldError> = {};
    const errorsRecord = formState.errors as Record<string, any>;

    // Include flattened errors
    Object.keys(formState.errors || {}).forEach(key => {
      // Only include errors that match the pattern "groupIndex.fieldName"
      if (/^\d+\.\w+$/.test(key)) {
        allErrors[key] = errorsRecord[key] as FieldError;
      }
    });

    // Also check nested format
    if (formState.errors?.groups) {
      Object.keys(formState.errors.groups).forEach(groupIndexStr => {
        const groupIndex = Number(groupIndexStr);
        const groupData = formState.errors?.groups?.[groupIndex]?.data as
          | Record<string, any>
          | undefined;

        if (groupData) {
          Object.keys(groupData).forEach(field => {
            if (groupData[field]) {
              const key = `${groupIndex}.${field}`;
              // Only add if not already added from flattened format
              if (!allErrors[key]) {
                allErrors[key] = groupData[field] as FieldError;
              }
            }
          });
        }
      });
    }

    return allErrors;
  };

  const isGroupValid = (groupIndex: number, requiredFields: (keyof T)[]): boolean => {
    if (hasGroupErrors(groupIndex)) {
      return false;
    }
    
    const group = reform.getGroups()[groupIndex];
    if (!group) return false;
    
    return requiredFields.every(field => {
      const value = group.data[field];
      
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      
      if (value === null || value === undefined) {
        return false;
      }
      
      if (typeof value === 'string') {
        return value.trim() !== '';
      }
      
      return true;
    });
  };
  
  const areAllGroupsValid = (requiredFields: (keyof T)[]): boolean => {
    const groups = reform.getGroups();
    return groups.every((_, index) => isGroupValid(index, requiredFields));
  };
  
  const isFormSubmittable = (requiredFields: (keyof T)[]): boolean => {
    return Object.keys(formState.errors || {}).length === 0 && areAllGroupsValid(requiredFields);
  };
  
  const canAddNewGroup = (requiredFields: (keyof T)[]): boolean => {
    return areAllGroupsValid(requiredFields);
  };

  return {
    errors: getAllErrors(),
    hasErrors: Object.keys(formState.errors || {}).length > 0,
    getFieldError,
    getGroupErrors,
    getFieldErrors,
    clearErrors,
    clearFieldError,
    clearGroupError,
    clearGlobalError,
    // setFieldError,
    // setGroupError,
    // setGlobalError,
    getError,
    hasGroupErrors,
    isGroupValid,
    areAllGroupsValid,
    isFormSubmittable,
    canAddNewGroup,
  };
};
